import { CustomFieldDto, CustomFieldValues } from "@/store/custom-fields/types";
import { NotifyNotificationRules } from "@/store/site-monitor/types";
import RichTextEditorPlaceholder from "@/types/RichTextEditorPlaceholder";
import ContactGroup from "@/types/sv-data/contact-groups/ContactGroup";
import EventOutcomeAndNote from "@/types/sv-data/EventOutcomeAndNote";
import EmailNotificationRequest from "@/types/sv-data/events/EmailNotificationRequest";
import { EventNotification } from "@/types/sv-data/notification-rule/EventNotification";
import NotificationRule from "@/types/sv-data/notification-rule/NotificationRule";
import NotificationTemplate from "@/types/sv-data/notification-templates/NotificationTemplate";
import NotificationTemplateFormatOption, { notificationTemplateFormats } from "@/types/sv-data/notification-templates/NotificationTemplateFormatOption";
import { NotificationTemplateManualField } from "@/types/sv-data/notification-templates/NotificationTemplateManualField";
import { hasEmptyRequiredCustomFields, hasInvalidCustomFieldValues } from "@/utils/CustomFieldLogic";
import { isEqual, omit } from "lodash";

export const NotificationNoteMaxLength = 5000;
export const NoNotificationTemplateErrorIdentifier = -1;

export const BlankNotification: Notification = {
    contacts: [],
    contactGroups: [],
    note: "",
    attachSitRep: false,
    notificationTemplate: null,
    notificationManualFields: [],
    notificationCustomFields: [],
    notificationTemplateFormat: null,
    templateNote: ""
};

export const NotificationValidationMessages = {
    NOTE_TOO_LONG: `Note must not exceed ${NotificationNoteMaxLength} characters.`,
    NO_CONTACTS_OR_GROUPS: "You must specify at least one contact or contact group.",
    EVENT_OUTCOME_INVALID: "Event outcome is not valid.",
    REQUIRED_CUSTOM_FIELDS_EMPTY: "Required custom fields are empty.",
    INVALID_CUSTOM_FIELDS: "Custom fields have invalid values.",
    TEMPLATE_FORMAT_NOT_SELECTED: "Template format is not selected.",
    NOTIFICATION_TEMPLATE_MISSING: "Notification template is missing."
};

const NotificationComparisonProperties = {
    SKIPPED: "skipped",
    PREVIOUSLY_SENT: "isPreviouslySent",
    CONTACTS_DIFFERENT_TO_RULE: "hasContactsDifferentToRule",
    CONTACT_GROUPS: "contactGroups",
    NOTE: "note",
}

export interface SkippedRequiredNotificationTemplate {
    notificationTemplateId: number;
    notificationTemplateName: string;
}

export const NotificationPlaceholderDefaults: RichTextEditorPlaceholder[] = [
	{
		id: "areaName",
		label: "Affected Area",
	},
	{
		id: "areaAddress",
		label: "Area Address",
	},
	{
		id: "areaReferenceId",
		label: "Area Unique Identifier",
	},
	{
		id: "eventCreatedDate",
		label: "Event Started",
	},
	{
		id: "alarms",
		label: "Alarms",
	},
	{
		id: "eventOutcome",
		label: "Event Outcome",
	},
	{
		id: "eventOutcomeNote",
		label: "Event Outcome Note",
	},
	{
		id: "note",
		label: "Note",
	},
	{
		id: "senderName",
		label: "Sender Name",
	},
	{
		id: "eventUsers",
		label: "Event Users",
	},
	{
		id: "eventId",
		label: "Event ID",
	},
];

export interface ContactOption {
	email: string;
	fullName: string | null;
	userID?: number;
}

export interface NotificationValidation {
    templateId: number | null;

    // Error cases for all templates
    noteTooLong: boolean;
    noContactsOrContactGroups: boolean;
    eventOutcomeInvalid: boolean;

    // Errors pertaining to a Notification with a Notification Template
    notificationTemplateErrors: NotificationTemplateValidation | null;
}

export interface ManualFieldInput {
	field: NotificationTemplateManualField;
	value: string
    valuePrePopulatedFromEmail?: boolean;
}

export interface Notification {
	notificationTemplate: NotificationTemplate;
    notificationTemplateFormat: NotificationTemplateFormatOption;

    contacts: ContactOption[];
	contactGroups: ContactGroup[];

	note: string;
	attachSitRep: boolean;
	templateNote: string;

    notificationManualFields: ManualFieldInput[];
	notificationCustomFields: ManualFieldInput[];
}

export interface NotificationState extends Notification {
    skipped?: boolean;
    isPreviouslySent?: boolean;
    hasContactsDifferentToRule?: boolean;
}

interface NotificationTemplateValidation {
    requiredCustomFieldsLeftEmpty: boolean;
    customFieldsContainInvalidValues: boolean;
    templateFormatTypeNotSelected: boolean;
}

export const NotificationMixins = {
    conversionMethods: {
        /**
         * Safely converts an EventNotification object to a Notification object.
         * Handles JSON parsing and maps EmailNotificationRequest structure to Notification structure.
         * @param eventNotification - The EventNotification from the database (contains EmailNotificationRequest as JSON)
         * @param allTemplates - Array of all notification templates to lookup the template object
         * @returns Parsed Notification object or null if parsing fails
         */
        eventNotificationToNotification(
            eventNotification: EventNotification,
            allTemplates: NotificationTemplate[] = []
        ): Notification | null {
            if (!eventNotification || !eventNotification.notificationData) {
                return null;
            }

            try {
                const emailRequest = JSON.parse(eventNotification.notificationData) as EmailNotificationRequest;

                // Find the template from the database if available
                let notificationTemplate: NotificationTemplate | null = null;
                if (eventNotification.notificationTemplateId && allTemplates.length > 0) {
                    notificationTemplate = allTemplates.find(t => t.notificationTemplateId === eventNotification.notificationTemplateId) || null;
                }

                // Convert EmailNotificationRequest to Notification structure
                const notification: Notification = {
                    contacts: emailRequest.contacts?.map(c => ({
                        email: c.email,
                        fullName: c.fullName,
                        userID: undefined
                    })),
                    contactGroups: emailRequest.contactGroups || [],
                    note: emailRequest.note || "",
                    attachSitRep: emailRequest.attachSitRep || false,
                    notificationTemplate: notificationTemplate,
                    notificationManualFields: [],
                    notificationCustomFields: [],
                    notificationTemplateFormat: null,
                    templateNote: ""
                };

                // Map createNotificationDocumentRequest fields to notification fields if present
                if (emailRequest.createNotificationDocumentRequest) {
                    const docRequest = emailRequest.createNotificationDocumentRequest;

                    // Set notification template format from notificationDocumentTypeId
                    if (docRequest.notificationDocumentTypeId !== null && docRequest.notificationDocumentTypeId !== undefined) {
                        const matchingFormat = notificationTemplateFormats.find(f => f.id === docRequest.notificationDocumentTypeId);
                        if (matchingFormat) {
                            notification.notificationTemplateFormat = matchingFormat;
                        }
                    }

                    // Convert manualInputFields to ManualFieldInput array
                    if (notificationTemplate && notificationTemplate.manualFields && docRequest.manualInputFields) {
                        notification.notificationManualFields = notificationTemplate.manualFields?.map(field => ({
                            field: field,
                            value: docRequest.manualInputFields[field.id] || "",
                            valuePrePopulatedFromEmail: !!docRequest.manualInputFields[field.id]
                        }));
                    }

                    // Convert customFieldInputs to ManualFieldInput array
                    if (notificationTemplate && notificationTemplate.customFields && docRequest.customFieldInputs) {
                        notification.notificationCustomFields = notificationTemplate.customFields?.map(field => ({
                            field: field,
                            value: docRequest.customFieldInputs[field.id] || ""
                        }));
                    }
                }

                return notification;
            } catch (err) {
                console.error("[eventNotificationToNotification] Error parsing EventNotification to Notification:", err);
                return null;
            }
        },
        /**
         * Converts a Notification object into the EmailNotificationRequest type for API consumption.
         * @param notification - Notification object to convert
         * @returns Parsed EmailNotificationRequest object to be passed to the Data Service
         */
        notificationToEmailRequest(notification: Notification): EmailNotificationRequest {
            return {
                contacts: notification.contacts?.map(c => ({
                    fullName: c.fullName,
                    email: c.email
                })),
                contactGroupIds: notification.contactGroups?.map(cg => cg.contactGroupId),
                contactGroups: notification.contactGroups,
                note: notification.note,
                attachSitRep: notification.attachSitRep,
                createNotificationDocumentRequest: notification.notificationTemplate
                    ? {
                        notificationTemplateId: notification.notificationTemplate.notificationTemplateId,
                        notificationDocumentTypeId: notification.notificationTemplateFormat?.id ?? 0,
                        note: notification.note,
                        manualInputFields: notification.notificationManualFields.reduce((map, obj) => {
                            map[obj.field.id] = obj.value;
                            return map;
                        }, {}),
                        customFieldInputs: notification.notificationCustomFields.reduce((map, obj) => {
                            map[obj.field.id] = obj.value;
                            return map;
                        }, {})
                    }
                    : null
            };
        },
    },
    methods: {
        // Shared utility for parsing notificationData
        parseNotificationData(template: EventNotification): NotificationState | null {
            if (!template || !template.notificationData) return null;
            try {
                return JSON.parse(template.notificationData);
            } catch (err) {
                console.error("Error parsing notificationData:", err);
                return null;
            }
        },
        /**
         * Loads data from a template (saved or previously sent) into a notification.
         * Populates manual/custom fields, contacts, contact groups, note, attachSitRep, and template format.
         * @param notification - Target notification to populate
         * @param existingNotificationData - Source notification data (from saved or previously-sent template)
         * @param allContacts - All available contacts for reference matching
         * @param allContactGroups - All available contact groups for reference matching
         */
        loadDataFromTemplate(
            notification: NotificationState,
            existingNotificationData: Notification | null,
            allContacts: ContactOption[] = [],
            allContactGroups: ContactGroup[] = []
        ): void {
            // Use template from the data (works for both saved and sent)
            const template = existingNotificationData?.notificationTemplate || notification.notificationTemplate;

            // Populate manual and custom fields
            this.populateManualAndCustomFields(notification, template, existingNotificationData);

            // Bind contacts and contact groups
            this.bindContactsFromPreviousNotification(notification, existingNotificationData, allContacts, allContactGroups);

            // Set note
            if (existingNotificationData?.note ) {
                notification.note = existingNotificationData.note;
            }

            // Set attachSitRep
            if (existingNotificationData?.attachSitRep ) {
                notification.attachSitRep = existingNotificationData.attachSitRep;
            }

            // Set template format
            if (existingNotificationData?.notificationTemplateFormat?.id) {
                const matchingFormat = notificationTemplateFormats.find(f => f.id === existingNotificationData.notificationTemplateFormat.id);
                if (matchingFormat) {
                    notification.notificationTemplateFormat = matchingFormat;
                }
            }
        },

        // Shared helper for populating manual and custom fields
        populateManualAndCustomFields(
            notification: NotificationState,
            template: NotificationTemplate | null | undefined,
            savedOrPreviouslySentData: NotificationState | null
        ): void {
            const availableManualFields: ManualFieldInput[] = [];
            const availableCustomFields: ManualFieldInput[] = [];

            // Manual fields
            const manualFields = template?.manualFields;

            if (manualFields != null) {
                manualFields.forEach(element => {
                    let value = "";
                    if (savedOrPreviouslySentData?.notificationManualFields != null) {
                        // Find matching field in the ManualFieldInput array
                        const existingField = savedOrPreviouslySentData.notificationManualFields.find(f => f.field.id === element.id);
                        if (existingField && existingField.value != null) {
                            value = existingField.value;
                        }
                    }
                    availableManualFields.push({
                        field: element,
                        value: value,
                        valuePrePopulatedFromEmail: value ? true : false
                    });
                });
            }

            // Custom fields
            const customFields = template?.customFields;

            if (customFields != null) {
                customFields.forEach(element => {
                    let value = "";
                    if (savedOrPreviouslySentData?.notificationCustomFields != null) {
                        // Find matching field in the ManualFieldInput array
                        const existingField = savedOrPreviouslySentData.notificationCustomFields.find(f => f.field.id === element.id);
                        if (existingField && existingField.value != null) {
                            value = existingField.value;
                        }
                    }
                    const doesValueExist = availableCustomFields.some(f => f.field.id === element.id && f.value && f.value.length > 0);
                    if (doesValueExist) {
                        return;
                    }
                    availableCustomFields.push({
                        field: element,
                        value: value
                    });
                });
            }

            notification.notificationManualFields = availableManualFields;
            notification.notificationCustomFields = availableCustomFields;
        },
        /**
         * Compares two notifications while ignoring metadata properties.
         * Useful for detecting actual content changes vs. state changes.
         */
        notificationsAreEqual(
            notification1: NotificationState,
            notification2: NotificationState,
            contactGroupsFeatureEnabled: boolean | null = true,
            notificationNoteFeatureEnabled: boolean | null = true
        ): boolean {
            const propsToIgnore = [NotificationComparisonProperties.SKIPPED, NotificationComparisonProperties.PREVIOUSLY_SENT, NotificationComparisonProperties.CONTACTS_DIFFERENT_TO_RULE];

            if (!contactGroupsFeatureEnabled) {
                propsToIgnore.push(NotificationComparisonProperties.CONTACT_GROUPS);
            }

            if (!notificationNoteFeatureEnabled) {
                propsToIgnore.push(NotificationComparisonProperties.NOTE);
            }

            return isEqual(
                omit(notification1, propsToIgnore),
                omit(notification2, propsToIgnore)
            );
        },
        notificationHasErrors(validation: NotificationValidation): boolean {
            let templateErrors = false;
            let notificationErrors = false;
            notificationErrors =
                validation.noteTooLong ||
                validation.noContactsOrContactGroups ||
                validation.eventOutcomeInvalid;

            if (validation.templateId != null) {
                templateErrors =
                    validation.notificationTemplateErrors.customFieldsContainInvalidValues ||
                    validation.notificationTemplateErrors.requiredCustomFieldsLeftEmpty ||
                    validation.notificationTemplateErrors.templateFormatTypeNotSelected;
            }

            return notificationErrors || templateErrors;
        },
        /* Combine two arrays and remove duplicates */
        combineAndRemoveDuplicates<T>(array1: T[], array2: T[]): T[] {
            return combineAndRemoveDupes(array1, array2);
        },
        getCustomFieldValuesFromNotification(notification: Notification): CustomFieldValues[] {
            if (!notification || !notification.notificationCustomFields) return [];
            return notification.notificationCustomFields?.map(cf => ({
                id: cf.field.id,
                value: cf.value
            }));
        },
        validateNotification(
            notification: NotificationState,
            eventOutcome: EventOutcomeAndNote,
            hasCustomFieldPermissions: boolean,
            orderedCustomFieldList: CustomFieldDto[],
            notificationValidationErrorsByTemplate: Map<number, NotificationValidation> = new Map<number, NotificationValidation>()) {
            if (!notification) {
                return;
            }
            const notificationErrors = {
                templateId: notification.notificationTemplate ? notification.notificationTemplate.notificationTemplateId : null,

                noteTooLong: false,
                noContactsOrContactGroups: false,
                eventOutcomeInvalid: false,
                notificationTemplateErrors: {
                    requiredCustomFieldsLeftEmpty: false,
                    customFieldsContainInvalidValues: false,
                    templateFormatTypeNotSelected: false
                } as NotificationTemplateValidation
            } as NotificationValidation;

            if (notification == null) {
                return;
            }

            if (notification.skipped) {
                return;
            }
            // Note length check
            if (notification.note && notification.note.length > NotificationNoteMaxLength) {
                notificationErrors.noteTooLong = true;
            }

            // Contacts/Contact Groups check
            const hasContacts = notification.contacts && notification.contacts.length > 0;
            const hasGroups = notification.contactGroups && notification.contactGroups.length > 0;

            if (!hasContacts && !hasGroups) {
                notificationErrors.noContactsOrContactGroups = true;
            }

            // Template checks
            if (notification.notificationTemplate != null) {
                if (!eventOutcome?.valid) {
                    notificationErrors.eventOutcomeInvalid = true;
                }
                if (hasCustomFieldPermissions && orderedCustomFieldList && orderedCustomFieldList.length > 0) {
                    const [requiredCustomFieldsLeftEmpty, customFieldsContainInvalidValues] = this.checkCustomFieldValidation(notification, orderedCustomFieldList);
                    notificationErrors.notificationTemplateErrors.requiredCustomFieldsLeftEmpty = requiredCustomFieldsLeftEmpty;
                    notificationErrors.notificationTemplateErrors.customFieldsContainInvalidValues = customFieldsContainInvalidValues;
                }
                if (notification.notificationTemplateFormat === null) {
                    notificationErrors.notificationTemplateErrors.templateFormatTypeNotSelected = true;
                }
            }

            // Predefine Template notifications when ending an event, require a template
            // Therefore, the key can be safely assigned to their Template ID
            notificationValidationErrorsByTemplate.set(
                notification.notificationTemplate?.notificationTemplateId,
                notificationErrors
            );
        },
        checkCustomFieldValidation(
            notification: NotificationState,
            orderedCustomFieldList: CustomFieldDto[]
        ): [boolean, boolean] {
            const fieldsToCheck = notification.notificationCustomFields?.map(cf => findCustomField(cf.field, orderedCustomFieldList)).filter(cf => !!cf);
            const customFieldValues = this.getCustomFieldValuesFromNotification(notification);
            const requiredCustomFieldsLeftEmpty = hasEmptyRequiredCustomFields(customFieldValues, fieldsToCheck);
            const customFieldsContainInvalidValues = hasInvalidCustomFieldValues(customFieldValues, fieldsToCheck);

            return [requiredCustomFieldsLeftEmpty, customFieldsContainInvalidValues];
        },
        updateNotificationValidationErrors(
            notificationsToSend: NotificationState[],
            eventOutcome: EventOutcomeAndNote,
            hasCustomFieldPermissions: boolean,
            notifyNotificationRules: NotifyNotificationRules,
            notificationValidationErrorsByTemplate: Map<number, NotificationValidation>): void {
                notificationValidationErrorsByTemplate.clear();
                notificationsToSend.forEach(notification => {
                    this.validateNotification(notification,
                        eventOutcome,
                        hasCustomFieldPermissions,
                        notifyNotificationRules,
                        notificationValidationErrorsByTemplate);
                });
        },
        createNotification(
            userIds: number[] = [],
            contactGroupIds: string[] = [],
            emailAddresses: string[] = [],
            allContacts: ContactOption[] = [],
            contactGroups: ContactGroup[] = [],
            allNotificationTemplates: NotificationTemplate[] = [],
            notificationTemplateId: number | null = null,
            attachSitRep: boolean = false
        ): NotificationState {
            const notification: Notification = {
                contacts: [],
                contactGroups: [],
                note: "",
                attachSitRep: attachSitRep,
                notificationTemplate: null,
                notificationManualFields: [],
                notificationCustomFields: [],
                notificationTemplateFormat: null,
                templateNote: ""
            };

            if (userIds.length && allContacts.length) {
                notification.contacts = userIds?.map(id => allContacts.firstOrDefault(c => c.userID === id));
            }

            // Create new ContactOption entries for any email addresses provided that do not correspond to existing contacts
            emailAddresses.forEach(email => {
                if (!notification.contacts.some(c => c.email === email)) {
                    notification.contacts.push({
                        email: email,
                        fullName: null
                    });
                }
            });

            if (contactGroupIds.length && contactGroups.length) {
                notification.contactGroups = contactGroupIds?.map(id => contactGroups.firstOrDefault(g => g.contactGroupId === id));
            }
            if (notificationTemplateId && allNotificationTemplates.length) {
                notification.notificationTemplate = allNotificationTemplates.firstOrDefault(t => t.notificationTemplateId === notificationTemplateId);
            }

            const notificationState = notification as NotificationState;
            notificationState.skipped = false;

            return notificationState;
        },
        /**
         * Creates a blank notification with no contacts, groups, or template.
         * Useful for ad-hoc notification scenarios.
         */
        createBlankNotification(attachSitRep: boolean = false): NotificationState {
            return this.createNotification([], [], [], [], [], [], null, attachSitRep);
        },
        /*
        Group contacts and contact groups by their notification template ID
            Determines the Contact IDs and Contact Group IDs belonging to rules without a notification template
            Determines the Contact IDs and Contact Group IDs that belong to (potentially duplicate) notification templates from rules
            Returns ID values reflecting these values
        */
        groupContactsAndGroupsByTemplate(rules: NotificationRule[]): {
            contactsByTemplate: Map<number, number[]>,
            contactGroupsByTemplate: Map<number, string[]>,
            contactsWithNoTemplate: number[],
            contactGroupsWithNoTemplate: string[],
            emailAddressesWithNoTemplate: string[],
        } {
            const contactsByTemplate = new Map<number, number[]>();
            const contactGroupsByTemplate = new Map<number, string[]>();
            const contactsWithNoTemplate: number[] = [];
            const contactGroupsWithNoTemplate: string[] = [];
            const emailAddressesWithNoTemplate: string[] = [];

            if (!rules || rules.length === 0) {
                return {
                    contactsByTemplate,
                    contactGroupsByTemplate,
                    contactsWithNoTemplate: [],
                    contactGroupsWithNoTemplate: [],
                    emailAddressesWithNoTemplate: []
                };
            }
            rules.forEach(rule => {
                // If rule is associated to a Notification Template
                if (rule.notificationTemplateId) {
                    const tid = rule.notificationTemplateId;
                    // Add the rule's Contact IDs to the map entry for this template ID
                    contactsByTemplate.set(
                        tid,
                        combineAndRemoveDupes(contactsByTemplate.get(tid) || [], rule.userIds)
                    );
                    // Add the rule's Contact Group IDs to the map entry for this template ID
                    contactGroupsByTemplate.set(
                        tid,
                        combineAndRemoveDupes(contactGroupsByTemplate.get(tid) || [], rule.contactGroupIds)
                    );
                } else {
                    // If the rule doesn't have a template, add its Contact IDs and Contact Group IDs to the 'no template' arrays
                    contactsWithNoTemplate.push(...rule.userIds);
                    contactGroupsWithNoTemplate.push(...rule.contactGroupIds);
                    emailAddressesWithNoTemplate.push(...rule.emailAddresses);
                }
            });
            return {
                contactsByTemplate,
                contactGroupsByTemplate,
                contactsWithNoTemplate: combineAndRemoveDupes([], contactsWithNoTemplate),
                contactGroupsWithNoTemplate: combineAndRemoveDupes([], contactGroupsWithNoTemplate),
                emailAddressesWithNoTemplate: combineAndRemoveDupes([], emailAddressesWithNoTemplate)
            };
        },
        /*
            Groups all Contact Groups, Contact IDs, and Email Addresses
            Used when Predefined Templates feature is disabled
            Returns ID values reflecting these values
        */
        groupContactsAndGroups(rules: NotificationRule[]): {
            contactsByTemplate: Map<number, number[]>,
            contactGroupsByTemplate: Map<number, string[]>,
            contactsWithNoTemplate: number[],
            contactGroupsWithNoTemplate: string[],
            emailAddressesWithNoTemplate: string[],
        } {
            const contactsByTemplate = new Map<number, number[]>();
            const contactGroupsByTemplate = new Map<number, string[]>();
            const contactsWithNoTemplate: number[] = [];
            const contactGroupsWithNoTemplate: string[] = [];
            const emailAddressesWithNoTemplate: string[] = [];

            if (!rules || rules.length === 0) {
                return {
                    contactsByTemplate,
                    contactGroupsByTemplate,
                    contactsWithNoTemplate: [],
                    contactGroupsWithNoTemplate: [],
                    emailAddressesWithNoTemplate: []
                };
            }

            // Since Predefined Templates are disabled, all contacts and groups belong to 'no template'
            rules.forEach(rule => {
                contactsWithNoTemplate.push(...rule.userIds);
                contactGroupsWithNoTemplate.push(...rule.contactGroupIds);
                emailAddressesWithNoTemplate.push(...rule.emailAddresses);
            });

            // We return empty maps for contactsByTemplate and contactGroupsByTemplate
            // This is to allow for simplified handling in the calling code

            return {
                contactsByTemplate,
                contactGroupsByTemplate,
                contactsWithNoTemplate: combineAndRemoveDupes([], contactsWithNoTemplate),
                contactGroupsWithNoTemplate: combineAndRemoveDupes([], contactGroupsWithNoTemplate),
                emailAddressesWithNoTemplate: combineAndRemoveDupes([], emailAddressesWithNoTemplate)
            };
        },

        /**
         * Finds a matching contact from allContacts array, or creates a new one if not found.
         * This ensures v-select uses the same object reference for proper selection recognition.
         */
        findOrCreateContact(contact: ContactOption, allContacts: ContactOption[]): ContactOption {
            const existingContact = allContacts.find(c =>
                (contact.userID != null && c.userID === contact.userID) ||
                (contact.email != null && c.email === contact.email)
            );

            return existingContact || {
                email: contact.email,
                fullName: contact.fullName,
                userID: contact.userID
            };
        },
        /**
         * Finds a matching contact group from allContactGroups array, or creates a new one if not found.
         * This ensures v-select uses the same object reference for proper selection recognition.
         */
        findOrCreateContactGroup(contactGroup: ContactGroup, allContactGroups: ContactGroup[]): ContactGroup {
            const existingGroup = allContactGroups.find(g => g.contactGroupId === contactGroup.contactGroupId);

            return existingGroup || {
                contactGroupId: contactGroup.contactGroupId,
                title: contactGroup.title,
                groupId: contactGroup.groupId,
                suiteTenantId: contactGroup.suiteTenantId
            };
        },
        bindContactsFromPreviousNotification(
            notification: NotificationState,
            previouslySentTemplate: Notification,
            allContacts: ContactOption[] = [],
            allContactGroups: ContactGroup[] = []
        ): void {
            if (!previouslySentTemplate) {
                return;
            }

            // If the notification object has contacts or contact groups, it was created by a rule
            const notificationCreatedByRule = notification.contacts?.length !== 0 || notification.contactGroups?.length !== 0;

            if (previouslySentTemplate.contacts && Array.isArray(previouslySentTemplate.contacts)) {
                const newContacts = previouslySentTemplate.contacts
                    .filter((contact =>
                        notification.contacts?.length === 0 ||
                        ( contact.email != null && notification.contacts.some(notiContact => notiContact.email !== contact.email) ||
                        (contact.userID != null && notification.contacts.some(notiContact => notiContact.userID !== contact.userID)
                    ))))
                    ?.map(contact => this.findOrCreateContact(contact, allContacts));

                if (newContacts.length > 0) {
                    notification.hasContactsDifferentToRule = notificationCreatedByRule;
                    notification.contacts = notification.contacts.concat(newContacts);
                }
            }

            if (previouslySentTemplate.contactGroups && Array.isArray(previouslySentTemplate.contactGroups)) {
                const currentGroupIds = notification.contactGroups?.map(cg => cg.contactGroupId);
                const newContactGroups = previouslySentTemplate.contactGroups
                    .filter((contactGroup =>
                        currentGroupIds?.length === 0 ||
                        !currentGroupIds.includes(contactGroup.contactGroupId)
                    ))
                    ?.map(contactGroup => this.findOrCreateContactGroup(contactGroup, allContactGroups));

                if (newContactGroups.length > 0) {
                    notification.hasContactsDifferentToRule = notificationCreatedByRule;
                    notification.contactGroups = notification.contactGroups.concat(newContactGroups);
                }
            }

            notification.contacts = combineAndRemoveDupesByKeys(notification.contacts, [], ["email", "fullName"]);
            notification.contactGroups = combineAndRemoveDupesByKeys(notification.contactGroups, [], ["contactGroupId"]);
        },

    }
}

function findCustomField(customField: NotificationTemplateManualField, customFieldList: CustomFieldDto[]): CustomFieldDto {
    return customFieldList.find(cf => cf.id == customField.id);
}

function combineAndRemoveDupes<T>(array1: T[], array2: T[]): T[] {
    return Array.from(new Set((array1 || []).concat(array2 || [])));
}

// combineAndRemoveDupes for objects based on one or more key values
export function combineAndRemoveDupesByKeys<T>(array1: T[], array2: T[], keys: (keyof T)[]): T[] {
    // Example usage:
    // const result = combineAndRemoveDupesByKeys(array1, array2, ['id', 'name']);

    const combinedArray = (array1 || []).concat(array2 || []);
    const uniqueMap = new Map<string, T>();
    combinedArray.forEach(item => {
        const key = keys.map(k => item[k]).join("|");
        if (!uniqueMap.has(key)) {
            uniqueMap.set(key, item);
        }
    });
    return Array.from(uniqueMap.values());
}
